/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { IconButton } from '@coursera/cds-core';
import { ChevronNextIcon, ChevronPreviousIcon } from '@coursera/cds-icons';

import _t from 'i18n!nls/account-settings';

const styles = {
  /* When the tab bar overflows, fade the edge that has off-screen tabs and overlay a
     scroll button — CDS Tabs hardcode scrollButtons="off" and don't expose the prop, so
     without this an off-screen tab is unreachable by mouse. Each fade + button renders
     only while more tabs remain in that direction. */
  affordance: css`
    position: absolute;
    inset-block: 0;
    display: flex;
    align-items: center;
    pointer-events: none;
  `,
  end: css`
    inset-inline-end: 0;
    padding-inline-start: var(--cds-spacing-400);
    background: linear-gradient(to right, transparent, var(--cds-color-neutral-background-primary) 60%);

    [dir='rtl'] & {
      background: linear-gradient(to left, transparent, var(--cds-color-neutral-background-primary) 60%);
    }
  `,
  start: css`
    inset-inline-start: 0;

    /* CDS's scrollable tablist overhangs the wrapper by 3px on the leading side (its
       margin-inline-start focus-outline hack), so scrolled tab text can peek to the left
       of the back chevron. Pull the fade 3px further leading to cover that sliver, with a
       matching padding so the button itself stays visually anchored to the content edge. */
    margin-inline-start: -3px;
    padding-inline-start: 3px;
    padding-inline-end: var(--cds-spacing-400);
    background: linear-gradient(to left, transparent, var(--cds-color-neutral-background-primary) 60%);

    [dir='rtl'] & {
      background: linear-gradient(to right, transparent, var(--cds-color-neutral-background-primary) 60%);
    }
  `,
  button: css`
    pointer-events: auto;
  `,
};

type Props = {
  canScrollBackward: boolean;
  canScrollForward: boolean;
  onScrollBackward: () => void;
  onScrollForward: () => void;
};

/**
 * Renders the leading/trailing edge fades and scroll chevrons for an overflowed tab bar.
 * Absolutely positioned, so it must live inside the element carrying `tabsWrapperRef`.
 */
export const TabScrollAffordance = ({
  canScrollBackward,
  canScrollForward,
  onScrollBackward,
  onScrollForward,
}: Props) => (
  <>
    {canScrollBackward && (
      <div css={[styles.affordance, styles.start]}>
        <IconButton
          size="small"
          variant="ghostSecondary"
          icon={<ChevronPreviousIcon />}
          aria-label={_t('Show previous tabs')}
          tooltip={_t('Show previous tabs')}
          onClick={onScrollBackward}
          css={styles.button}
        />
      </div>
    )}
    {canScrollForward && (
      <div css={[styles.affordance, styles.end]}>
        <IconButton
          size="small"
          variant="ghostSecondary"
          icon={<ChevronNextIcon />}
          aria-label={_t('Show more tabs')}
          tooltip={_t('Show more tabs')}
          onClick={onScrollForward}
          css={styles.button}
        />
      </div>
    )}
  </>
);
